import React from 'react';
import { Url } from '@wix/da-url';
import { Trans, useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiData,
  ShowDeviationAnywayClickBiEvent,
  OpenBrowsingModeClickBiEvent,
  RedirectToLoginBiEvent,
} from '@wix/da-bi/pkg/events';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import HiddenIcon from '@wix/da-ds/pkg/Icons/32x32/Hidden';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import HiddenLayout from './HiddenLayout';

import { HiddenProps } from './types';
import hiddenStyles from './Hidden.scss';

/** an overlay to cover nsfgoogle content. it handles three cases:
 *  logged-out users, logged-in underage users, and over 18 logged-in users */
const NsfgBlockedOverlay: React.FC<HiddenProps> = ({
  compact,
  isLoggedIn,
  isUserOlderThan18,
  overrideMatureFilter,
  pushUserSettingsModal,
  ...props
}) => {
  // note: unlike mature, which tests for and flags logged-out and underage users
  // on the server-side, nsfg does it here on the client
  const { t } = useTranslation();

  // logged out
  if (!isLoggedIn) {
    return (
      <HiddenLayout
        icon={<HiddenIcon size={IconSize.HUGE} />}
        text={t('deviationview.blocked.nsfg.text')}
        button={
          <GhostButton
            variant="main"
            size="medium"
            href={Url.joinLink('mature')}
            biData={BiData<RedirectToLoginBiEvent>({
              evid: 240,
              referral_info: ReferralType.NSFG,
            })}
          >
            {t('deviationview.blocked.nsfg.anon.signIn')}
          </GhostButton>
        }
        {...props}
      />
    );
  }

  // logged in, 13-18 and over 18
  const { deviation } = props;
  return (
    <HiddenLayout
      icon={<HiddenIcon size={IconSize.HUGE} />}
      text={t('deviationview.blocked.nsfg.text')}
      button={
        <GhostButton
          variant="main"
          size="medium"
          onClick={() => overrideMatureFilter(deviation.deviationId)}
          biData={BiData<ShowDeviationAnywayClickBiEvent>({
            evid: 305,
            typeid: deviation.typeId,
            itemid: deviation.deviationId,
          })}
        >
          {t('deviationview.blocked.showDeviationAnyway')}
        </GhostButton>
      }
      secondaryButton={
        <TextButton
          className={hiddenStyles['secondary-button']}
          size="medium"
          variant="main"
          onClick={pushUserSettingsModal}
          biData={BiData<OpenBrowsingModeClickBiEvent>({
            evid: 306,
            typeid: deviation.typeId,
            itemid: deviation.deviationId,
          })}
        >
          {/* this uses Trans because design wants a bolded component in the text */}
          {/* note: this needs `white-space: pre-wrap` on it to prevent losing whitespace */}
          <Trans i18nKey={'deviationview.blocked.openContentFilterModal'} />
        </TextButton>
      }
      {...props}
    />
  );
};
NsfgBlockedOverlay.displayName = 'NsfgBlockedOverlay';

export default NsfgBlockedOverlay;
